import React from 'react';
import axios from '../axios';
import Loader from '../loader';
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import moment from 'moment';
import AboutPaymentModal from '../redux/components/event/about_payment_modal';
import {
  PLAN_DESCRIPTION_MAX_LENGTH,
  isPlanDescriptionAtLimit,
  isPlanDescriptionTooLong,
} from './plan_description';

const MIN_PREPAID_PRICE = 500; // 事前決済の参加料金の下限値

const SectionPlans = ({
  eventId,
  reservationSystemFee,
  eventsPlans,
  setEventsPlans,
  isNew,
  isCompanyOwnedCircle,
  isReadOnly,
  tunagateFeeRate,
}) => {
  const [isCreating, setIsCreating] = React.useState(false);
  const [editableEventsPlanIds, setEditableEventsPlanIds] = React.useState([]);

  const initialEventsPlan = {
    id: '',
    event_id: eventId,
    plan: '参加チケット',
    target_type: 2,
    capacity: 0,
    price: MIN_PREPAID_PRICE + (reservationSystemFee || 0),
    creator_price: MIN_PREPAID_PRICE,
    price_type: 3,
    role_type: 0,
    description: '',
    expired_at: null,
    is_application_allowed: true,
  };

  const addEventsPlan = React.useCallback(() => {
    setIsCreating(true);
    axios
      .post('/api/event_edit/create_events_plan', initialEventsPlan)
      .then((response) => {
        setEventsPlans((prev) => [...prev, response.data]);
        setEditableEventsPlanIds((prev) => [...prev, response.data.id]);
      })
      .finally(() => {
        setIsCreating(false);
      });
  });

  const removeEventsPlan = React.useCallback(
    (planId, index) => {
      axios
        .post('/api/event_edit/delete_events_plan', {
          event_id: eventId,
          id: planId,
        })
        .then(() => {
          setEventsPlans((prev) => {
            const newEventsPlans = [...prev];
            newEventsPlans.splice(index, 1);
            return newEventsPlans;
          });
        });
    },
    [eventId]
  );

  return (
    <section id="plans">
      <style>{`
        .tg-ticket .shadow-sm{box-shadow:none;}
        .tg-ticket .border-bottom:last-child{border-bottom:none !important;}
        .tg-ticket-add:active{opacity:.72;}
        .tg-ticket-add:focus-visible{outline:2px solid #FF6467;outline-offset:2px;}
        .tg-ticket-remove:hover{color:#FF6467 !important;}
        .tg-ticket-remove:focus:not(:focus-visible){outline:none;}
        .tg-ticket-remove:focus-visible{outline:2px solid #FF6467;outline-offset:2px;}
      `}</style>
      <div className="container col-md-8 col-lg-6">
        <p style={{ margin: '12px 0 8px' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#666' }}>チケット</span>
        </p>
      </div>
      <div className="container col-md-8 col-lg-6">
        {eventsPlans.map((eventsPlan, index) => (
          <EventsPlanBox
            key={eventsPlan.id}
            eventsPlan={eventsPlan}
            setEventsPlan={(plan) => {
              setEventsPlans((prev) => {
                const newEventsPlans = [...prev];
                newEventsPlans[index] = plan;
                return newEventsPlans;
              });
            }}
            removeEventsPlan={() => {
              removeEventsPlan(eventsPlan.id, index);
            }}
            isEditable={editableEventsPlanIds.includes(eventsPlan.id) || isNew}
            reservationSystemFee={reservationSystemFee}
            isRemovable={eventsPlans.length > 1 && eventsPlan.is_removable}
            isCompanyOwnedCircle={isCompanyOwnedCircle}
            isReadOnly={isReadOnly}
            tunagateFeeRate={tunagateFeeRate}
          />
        ))}
      </div>
      {isReadOnly ? (
        // @context TGW-2195 提供終了した公式／サンプリングイベントは決済導線を復活させない
        //   ため、チケットの追加・削除・編集はさせず読み取り専用の案内だけを表示する。
        <div className="container col-md-8 col-lg-6">
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8B9097' }}>
            このイベントのチケットは提供終了のため変更できません
          </p>
        </div>
      ) : isCreating ? (
        <Loader isActive={isCreating} />
      ) : (
        <div className="container col-md-8 col-lg-6">
          {/* @context TGW-2195 一覧への追加操作を既存チケットカードと同じ幅・角丸へ揃え、
              小さな独立ボタンに見えていた階層と押しにくさを解消する。 */}
          <button
            type="button"
            className="tg-ticket-add"
            onClick={() => addEventsPlan()}
            style={{
              width: '100%',
              minHeight: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginTop: 4,
              background: '#fff',
              border: '1px solid #ececee',
              borderRadius: 16,
              color: '#FF6467',
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>＋</span>
            <span>チケットを追加</span>
          </button>
        </div>
      )}
    </section>
  );
};
const EventsPlanBox = ({
  eventsPlan,
  setEventsPlan,
  removeEventsPlan,
  isEditable,
  reservationSystemFee,
  isRemovable,
  isCompanyOwnedCircle,
  isReadOnly,
  tunagateFeeRate,
}) => {
  const modalRef = React.useRef();
  const isFirstMountRef = React.useRef(true);

  const updateEventsPlan = React.useCallback(() => {
    // @context TGW-2694 説明が長すぎると DB に弾かれて500になる。useEffect と onBlur の両方から
    //   呼ばれるので、送る直前のここで止める。
    if (isPlanDescriptionTooLong(eventsPlan.description)) {
      return;
    }
    axios.post('/api/event_edit/update_events_plan', eventsPlan);
  }, [eventsPlan]);

  React.useEffect(() => {
    // @context TGW-2195 マウント直後のこの effect が旧 state で
    //   updateEventsPlan() を叩き、サーバー側の最新値を上書きしてしまうため
    //   初回は保存しない（refで判定）。読み取り専用（提供終了の公式／
    //   サンプリングイベント）ではチケットを一切保存させない。
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    if (isReadOnly) {
      return;
    }
    // 事前決済 (price_type=3)で参加料金が下限値未満の場合は保存しない（サーバーも TGW-2839 で同じ下限を検査する）
    if (eventsPlan.price_type === 3 && Number(eventsPlan.creator_price) < MIN_PREPAID_PRICE) {
      return;
    }
    updateEventsPlan();
  }, [eventsPlan]);

  const previewContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', cursor: isReadOnly ? 'default' : 'pointer', textAlign: 'left' }}>
      <span style={{ fontSize: 14, fontWeight: 800, color: '#333', flex: 'none', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {eventsPlan.plan || '参加チケット'}
      </span>
      <span style={{ fontSize: 11, color: '#666', background: '#F2F2F2', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>
        {/* @context TGW-2839 price_type 0（未設定）・2（割り勘）まで「無料」と出ていたが、実際は参加料を現地で払う計算になる */}
        {eventsPlan.price_type === 3 ? '事前決済' : eventsPlan.price_type === 99 ? '無料' : '現地決済'}
        {Number(eventsPlan.capacity) > 0 ? ` · 定員${eventsPlan.capacity}` : ''}
      </span>
      <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: '#333', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
        {eventsPlan.price_type === 99 ? '無料' : `¥${Number(eventsPlan.creator_price || 0).toLocaleString()}`}
      </span>
      {!isReadOnly && <span style={{ color: '#8B9097', fontSize: 18, flex: 'none' }}>›</span>}
    </div>
  );

  return (
    <div
      className="mb-2 tg-ticket"
      style={{ border: '1px solid #ececee', borderRadius: 16, overflow: 'hidden', background: '#fff' }}
    >
      {isReadOnly ? (
        // @context TGW-2195 提供終了イベントは編集モーダルを開かせない
        //   （タップしても previewContent が表示のみになるよう
        //   ModalContentBaseXl 自体を使わない）。
        previewContent
      ) : (
        <ModalContentBaseXl
          ref={modalRef}
          previewContent={previewContent}
          modalContent={
            <PlanDetailsSettingModal
              closeModal={() => modalRef.current.closeModal()}
              eventsPlan={eventsPlan}
              setEventsPlan={setEventsPlan}
              isCompanyOwnedCircle={isCompanyOwnedCircle}
              reservationSystemFee={reservationSystemFee}
              isEditable={isEditable}
              updateEventsPlan={updateEventsPlan}
              tunagateFeeRate={tunagateFeeRate}
            />
          }
        />
      )}
      {isRemovable && !isReadOnly && (
        <div style={{ borderTop: '1px solid #ececee', textAlign: 'right' }}>
          {/* @context TGW-2195 「＋ チケットを追加」と同じく、記号と文言の間に隙間を入れて読みやすくする。 */}
          <button
            type="button"
            className="tg-ticket-remove"
            onClick={() => removeEventsPlan()}
            style={{ background: 'none', border: 'none', color: '#8B9097', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>−</span>
            チケットを削除
          </button>
        </div>
      )}
    </div>
  );
};

const PlanDetailsSettingModal = ({
  closeModal,
  eventsPlan,
  setEventsPlan,
  isCompanyOwnedCircle,
  reservationSystemFee,
  isEditable,
  updateEventsPlan,
  tunagateFeeRate,
}) => {
  const [isAgeRangeErrorOn, setIsAgeRangeErrorOn] = React.useState(false);
  const [
    isParticipationCountRangeErrorOn,
    setIsParticipationCountRangeErrorOn,
  ] = React.useState(false);

  React.useEffect(() => {
    if (
      eventsPlan.age_from &&
      eventsPlan.age_to &&
      eventsPlan.age_from > eventsPlan.age_to
    ) {
      setIsAgeRangeErrorOn(true);
    } else {
      setIsAgeRangeErrorOn(false);
    }
    if (
      eventsPlan.past_participation_count_from &&
      (eventsPlan.past_participation_count_to ||
        eventsPlan.past_participation_count_to === 0) &&
      eventsPlan.past_participation_count_from >
        eventsPlan.past_participation_count_to
    ) {
      setIsParticipationCountRangeErrorOn(true);
    } else {
      setIsParticipationCountRangeErrorOn(false);
    }
  }, [eventsPlan]);

  React.useEffect(() => {
    if (window.innerWidth >= 768) {
      $('.datetimepicker').each(function () {
        $(this).datetimepicker({
          format: 'Y/m/d H:i',
          lang: 'ja',
          scrollInput: true,
          timepicker: true,
          step: 5,
        });
      });
    }
  }, []);

  let datetimeFormType;
  let datetimeFormat;
  if (window.innerWidth >= 768) {
    datetimeFormType = 'text';
    datetimeFormat = 'YYYY/MM/DD HH:mm';
    $('.datetimepicker').each(function () {
      $(this).datetimepicker({
        format: 'Y/m/d H:i',
        lang: 'ja',
        scrollInput: true,
        timepicker: true,
        step: 5,
      });
    });
  } else {
    datetimeFormType = 'datetime-local';
    datetimeFormat = 'YYYY-MM-DDTHH:mm';
  }

  return (
    <div className="card bg-gray" style={{ paddingBottom: '144px' }}>
      <div
        className="bg-white w-100 border-bottom"
        style={{
          zIndex: 9998,
          borderRadius: '16px 16px 0 0',
          position: 'fixed',
        }}
      >
        <button
          type="button"
          className="close close__modal position--modalclose"
          aria-label="Close"
          onClick={() => closeModal()}
        >
          <span className="text-fz-24px float-right m-2" aria-hidden="true">
            ×
          </span>
        </button>
      </div>
      <div className="bg-white h-100">
        <main
          style={{ paddingBottom: '180px' }}
          className="bg-gray h-main100vh"
        >
          <div className="container col-md-8 col-lg-6 pt-5">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-0 d-inline-block">
              チケット名
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              早期に申し込むとお得になる「早割」チケットの活用は有効です。申込期限や人数で区切って早割りチケットを作りましょう。
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <input
                id="plan"
                className="p-0 w-100 textarea--eventreport"
                value={eventsPlan.plan}
                onBlur={() => updateEventsPlan()}
                onChange={(e) =>
                  setEventsPlan({ ...eventsPlan, plan: e.target.value })
                }
                placeholder="チケット名を入力(例：イベント+飲み会）"
              />
            </div>
          </div>
          {isCompanyOwnedCircle && (
            <React.Fragment>
              <div className="container col-md-8 col-lg-6">
                <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 mr-3 d-inline-block">
                  役割 ※つなげーと公式イベント限定設定
                </p>
              </div>
              <div className="shadow-sm mb-0 bg-white">
                <div className=" container col-md-8 col-lg-6 p-3_5">
                  <select
                    id="role_type"
                    onBlur={() => updateEventsPlan()}
                    onChange={(e) => {
                      setEventsPlan({
                        ...eventsPlan,
                        role_type: Number(e.target.value),
                      });
                    }}
                    value={eventsPlan.role_type}
                    className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                  >
                    <option value={0}>一般</option>
                    <option value={1}>ファシリテーター</option>
                  </select>
                </div>
              </div>
            </React.Fragment>
          )}
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              対象者
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 border-bottom p-3_5">
              <select
                id="target_type"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    target_type: Number(e.target.value),
                  });
                }}
                value={eventsPlan.target_type}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                disabled={!isEditable}
              >
                <option value={2}>サークルメンバー+非メンバー</option>
                <option value={1}>サークルメンバー</option>
                <option value={0}>非メンバー</option>
              </select>
            </div>
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <select
                id="capacity"
                placeholder="募集人数を選択"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    capacity: Number(e.target.value),
                  });
                }}
                value={eventsPlan.capacity}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
              >
                <option value={0}>人数制限なし</option>
                {[...Array(100)].map((_, i) => {
                  return (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}人
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          {eventsPlan.price_type !== 99 && (
            <div className="container col-md-8 col-lg-6">
              <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 mr-3 d-inline-block">
                会費
                {eventsPlan.price_type === 3 && `（${MIN_PREPAID_PRICE}円以上）`}
              </p>
              {/* <p className="text-fz-small text-black-50 mb-2">
              現地決済を設定せず、すべてのチケットが事前決済（クレジット /
              Amazon Pay）で設定されているイベントを表示上で優遇します。
            </p> */}
            </div>
          )}
          <div className="shadow-sm mb-0 bg-white">
            {eventsPlan.price_type === 99 ? null : (
              <div className="container col-md-8 col-lg-6 border-bottom p-3_5 d-flex justify-content-between">
                <div>
                  <input
                    id="creator_price"
                    className="p-0 textarea--eventreport"
                    type="text"
                    min={0}
                    value={eventsPlan.creator_price}
                    onBlur={(e) => {
                      setEventsPlan({
                        ...eventsPlan,
                        // @context TGW-2839 新しく設定できる料金は事前決済だけ。TW-4251 以前の現地払いチケット
                        //   （price_type 0/1/2）も料金を変えたら事前決済に切り替える（サーバーは料金を変えた非事前決済を弾く）
                        price_type: 3,
                        creator_price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) >= MIN_PREPAID_PRICE
                            ? Number(e.target.value)
                            : MIN_PREPAID_PRICE,
                        price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) >= MIN_PREPAID_PRICE
                            ? Number(e.target.value)
                            : MIN_PREPAID_PRICE,
                      });
                    }}
                    onChange={(e) =>
                      setEventsPlan({
                        ...eventsPlan,
                        price_type: 3, // TGW-2839 onBlur と同じ（入力途中の下限未満は上の useEffect が保存しない）
                        creator_price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                            ? Number(e.target.value)
                            : 0,
                        price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                            ? Number(e.target.value)
                            : 0,
                      })
                    }
                    placeholder="料金を入力(半角数字)"
                    style={{ width: '160px' }}
                    disabled={!isEditable}
                  />
                  円
                  {eventsPlan.price_type === 3 && (
                    <div className="text-fz-small text-black-50">
                      <div className="mt-1">
                        <div>クリエイターの手取り：</div>
                        <div>
                          <div>
                            {Math.round(
                              (eventsPlan.creator_price) *
                                (1 - tunagateFeeRate)
                            )}
                            円（振込で引き出しの場合）
                          </div>
                          <div>
                            {eventsPlan.creator_price}
                            円（ポイント引き出しの場合）
                          </div>
                        </div>
                      </div>
                      <div>
                        チケット金額は手数料を加算して表示される場合がございます
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {eventsPlan.price_type === 3 && (
              <div className=" container col-md-8 col-lg-6 p-3_5">
                <select
                  id="price_type"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      price_type: Number(e.target.value),
                    });
                  }}
                  value={eventsPlan.price_type}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                  disabled={true}
                >
                  {/* <option value={99}>無料（TOPや検索一覧に表示されません）</option> */}
                    <option value={3}>
                      事前決済（クレジット / Amazon Pay）
                    </option>
                  {/* <option value={1}>現地決済（TOPや検索一覧に表示されません）</option> */}
                </select>
              </div>
            )}
            {Number(eventsPlan.price_type) === 3 && (
              <div className="container col-md-8 col-lg-6 pb-3">
                <AboutPaymentModal />
              </div>
            )}
          </div>
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              チケットの説明
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              参加料金以外に現地で掛かる費用についてはここに記入してください。
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <input
                id="description"
                className="p-0 w-100 textarea--eventreport"
                value={eventsPlan.description}
                maxLength={PLAN_DESCRIPTION_MAX_LENGTH}
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({ ...eventsPlan, description: e.target.value });
                }}
                placeholder="説明が必要な場合は記入"
              />
              {isPlanDescriptionAtLimit(eventsPlan.description) && (
                <p className="text-danger text-fz-small mt-2 mb-0">
                  チケットの説明は{PLAN_DESCRIPTION_MAX_LENGTH}文字までです。イベントの詳細を記述する場合は、紹介文の欄にお願いします。
                </p>
              )}
            </div>
          </div>

          {/* <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              初回参加者の割引設定
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              このサークルのイベントに初めて参加する場合の割引率設定
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <select
                  id="first_participant_discount_rate"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const discount_rate = e.target.value;

                    if (discount_rate === '割引しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        first_participant_discount_rate: 0,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        first_participant_discount_rate: Number(
                          discount_rate.slice(0, -1)
                        ),
                      });
                    }
                  }}
                  value={
                    !eventsPlan.first_participant_discount_rate ||
                    eventsPlan.first_participant_discount_rate === 0
                      ? '割引しない'
                      : `${eventsPlan.first_participant_discount_rate}%`
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  {['割引しない', '100%', '50%', '30%', '20%', '10%'].map(
                    (i) => (
                      <option value={i} key={i}>
                        {i}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div> */}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              年齢制限
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              設定範囲外のユーザーは申し込みできなくなります
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <div className="text-fz-small text-black-50">上限</div>
                <select
                  id="age_to"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const age = e.target.value;

                    if (age === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        age_to: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        age_to: Number(age),
                      });
                    }
                  }}
                  value={eventsPlan.age_to || '設定しない'}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  {Array.from({ length: 100 - 14 }, (_, idx) => idx + 14).map(
                    (age) => {
                      if (
                        eventsPlan.age_from === null ||
                        eventsPlan.age_from === undefined
                      ) {
                        return (
                          <option value={age} key={age}>
                            {age}歳
                          </option>
                        );
                      } else {
                        if (age >= eventsPlan.age_from) {
                          return (
                            <option value={age} key={age}>
                              {age}歳
                            </option>
                          );
                        }
                      }
                    }
                  )}
                </select>
                <div className="text-fz-small text-black-50">下限</div>
                <select
                  id="age_from"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const age = e.target.value;

                    if (age === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        age_from: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        age_from: Number(age),
                      });
                    }
                  }}
                  value={eventsPlan.age_from || '設定しない'}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  {Array.from({ length: 100 - 14 }, (_, idx) => idx + 14).map(
                    (age) => {
                      if (
                        eventsPlan.age_to === null ||
                        eventsPlan.age_to === undefined
                      ) {
                        return (
                          <option value={age} key={age}>
                            {age}歳
                          </option>
                        );
                      } else {
                        if (age <= eventsPlan.age_to) {
                          return (
                            <option value={age} key={age}>
                              {age}歳
                            </option>
                          );
                        }
                      }
                    }
                  )}
                </select>
              </div>
            </div>
          </div>
          {isAgeRangeErrorOn && (
            <div
              className="container col-md-8 col-lg-6 pt-2"
              style={{ fontSize: '12px' }}
            >
              <span style={{ color: '#ff5252' }}>下限が上限より小さいです</span>
            </div>
          )}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              サークルの過去イベント参加回数
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              設定範囲外のユーザーは申し込みできなくなります
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <div className="text-fz-small text-black-50">上限</div>
                <select
                  id="past_participation_count_to"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const participationCount = e.target.value;

                    if (participationCount === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_to: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_to: Number(participationCount),
                      });
                    }
                  }}
                  value={
                    eventsPlan.past_participation_count_to ||
                    (eventsPlan.past_participation_count_to === 0
                      ? 0
                      : '設定しない')
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  <option value={0} key={0}>
                    初参加のみ
                  </option>
                  <option value={1} key={1}>
                    過去1回以下
                  </option>
                  <option value={2} key={2}>
                    過去2回以下
                  </option>
                  <option value={3} key={3}>
                    過去3回以下
                  </option>
                  <option value={5} key={5}>
                    過去5回以下
                  </option>
                  <option value={10} key={10}>
                    過去10回以下
                  </option>
                </select>
                <div className="text-fz-small text-black-50">下限</div>
                <select
                  id="past_participation_count_from"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const participationCount = e.target.value;

                    if (participationCount === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_from: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_from:
                          Number(participationCount),
                      });
                    }
                  }}
                  value={
                    eventsPlan.past_participation_count_from || '設定しない'
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  <option value={1} key={1}>
                    過去1回以上
                  </option>
                  <option value={2} key={2}>
                    過去2回以上
                  </option>
                  <option value={3} key={3}>
                    過去3回以上
                  </option>
                  <option value={5} key={5}>
                    過去5回以上
                  </option>
                  <option value={10} key={10}>
                    過去10回以上
                  </option>
                </select>
              </div>
            </div>
          </div>
          {isParticipationCountRangeErrorOn && (
            <div
              className="container col-md-8 col-lg-6 pt-2"
              style={{ fontSize: '12px' }}
            >
              <span style={{ color: '#ff5252' }}>下限が上限より大きいです</span>
            </div>
          )}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              このチケットの募集締切
              <span className="text-fz-small text-fw-normal">
                　※空欄の場合は無期限
              </span>
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <label
                htmlFor="expired_at"
                className="row justify-content-between p-3_5"
              >
                <p className="pr-2 mb-0">締切日時</p>
                <input
                  type={datetimeFormType}
                  className="datetimepicker form-style-reset p-0 textarea--eventreport"
                  id="expired_at"
                  value={
                    eventsPlan.expired_at
                      ? moment(eventsPlan.expired_at).format(datetimeFormat)
                      : ''
                  }
                  onBlur={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      expired_at: e.target.value,
                    });
                  }}
                  onChange={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      expired_at: e.target.value,
                    });
                  }}
                  placeholder="2019/08/25 10:00"
                  autoComplete="off"
                />
              </label>
              <div
                className="text-fz-small text-black-50 text-right"
                style={{ marginTop: '-10px', paddingBottom: 10 }}
              >
                <span
                  onClick={() => {
                    setEventsPlan({ ...eventsPlan, expired_at: '' });
                  }}
                >
                  日時をクリアする
                </span>
              </div>
            </div>
          </div>
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              このチケットの募集ステータス
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <select
                id="is_application_allowed"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    is_application_allowed: e.target.value === 'true',
                  });
                }}
                value={eventsPlan.is_application_allowed}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
              >
                <option value={true}>募集中</option>
                <option value={false}>募集終了</option>
              </select>
            </div>
          </div>
          <div style={{ paddingBottom: '12px' }}>
            <div className="container col-md-8 col-lg-6">
              <button
                type="button"
                className="a-button a-button__top a-button--orange w-100 mb-5 mt-5"
                onClick={() => closeModal()}
              >
                OK
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SectionPlans;
